import {
  DEFAULT_STOREFRONT_FOOTER_CHROME_CONFIG,
  sanitizeStorefrontFooterChromeConfig,
} from '../lib/storefront-engine/navigation';
import { WEBSITE_UUID_RE, isRecord } from '../lib/website-templates/internal';
import type { SupabaseClient } from '../supabase/client';
import type { Json } from '../supabase/database.types';
import type { StorefrontSocialLink } from '../types/storefront-engine';

export interface StoreSocialProfilesSettings {
  readonly profiles: readonly StorefrontSocialLink[];
  /** Exact PostgreSQL revision string; preserve its microseconds for CAS. */
  readonly updatedAt: string;
}

export interface UpdateStoreSocialProfilesInput {
  readonly storeId: string;
  readonly profiles: readonly StorefrontSocialLink[];
  readonly expectedUpdatedAt: string;
}

/** Reuses F081's complete shape, ID uniqueness, profile limit and safe URL validator. */
export function sanitizeStoreSocialProfiles(
  profiles: unknown,
): readonly StorefrontSocialLink[] | null {
  return (
    sanitizeStorefrontFooterChromeConfig({
      ...DEFAULT_STOREFRONT_FOOTER_CHROME_CONFIG,
      socialLinks: profiles,
    })?.socialLinks ?? null
  );
}

function parseSettings(value: unknown): StoreSocialProfilesSettings {
  if (
    !isRecord(value) ||
    Object.keys(value).length !== 2 ||
    !Object.hasOwn(value, 'profiles') ||
    !Object.hasOwn(value, 'updatedAt')
  )
    throw new Error('invalid social profiles response');
  const profiles = sanitizeStoreSocialProfiles(value.profiles);
  if (
    !profiles ||
    typeof value.updatedAt !== 'string' ||
    !Number.isFinite(Date.parse(value.updatedAt))
  )
    throw new Error('invalid social profiles response');
  return { profiles, updatedAt: value.updatedAt };
}

export async function getStoreSocialProfiles(
  client: SupabaseClient,
  storeId: string,
): Promise<StoreSocialProfilesSettings> {
  if (!WEBSITE_UUID_RE.test(storeId)) throw new Error('invalid social profiles request');
  const { data, error } = await client.rpc('get_store_social_profiles', { p_store_id: storeId });
  if (error) throw error;
  return parseSettings(data);
}

export async function updateStoreSocialProfiles(
  client: SupabaseClient,
  input: UpdateStoreSocialProfilesInput,
): Promise<StoreSocialProfilesSettings> {
  const profiles = sanitizeStoreSocialProfiles(input.profiles);
  if (
    !WEBSITE_UUID_RE.test(input.storeId) ||
    !profiles ||
    !Number.isFinite(Date.parse(input.expectedUpdatedAt))
  )
    throw new Error('invalid social profiles request');
  const profileJson: Json = profiles.map((profile) => ({
    id: profile.id,
    provider: profile.provider,
    url: profile.url,
  }));
  const { data, error } = await client.rpc('update_store_social_profiles', {
    p_store_id: input.storeId,
    p_profiles: profileJson,
    p_expected_updated_at: input.expectedUpdatedAt,
  });
  if (error) throw error;
  return parseSettings(data);
}

export type StoreSocialProfilesErrorKey =
  | 'admin.storeSettings.socialProfiles.stale'
  | 'admin.storeSettings.socialProfiles.forbidden'
  | 'admin.storeSettings.socialProfiles.invalid'
  | 'admin.storeSettings.socialProfiles.loadError'
  | 'admin.storeSettings.socialProfiles.saveError';

export function storeSocialProfilesErrorKey(
  error: unknown,
  stage: 'load' | 'save' = 'save',
): StoreSocialProfilesErrorKey {
  if (isRecord(error)) {
    if (error.code === '42501') return 'admin.storeSettings.socialProfiles.forbidden';
    if (error.code === '22023') return 'admin.storeSettings.socialProfiles.invalid';
    if (error.code === 'P0001' && error.message === 'stale_settings')
      return 'admin.storeSettings.socialProfiles.stale';
  }
  return stage === 'load'
    ? 'admin.storeSettings.socialProfiles.loadError'
    : 'admin.storeSettings.socialProfiles.saveError';
}
